import { CrossIcon, IdeaIcon } from '@/assets/icons'
import { DragEndEvent, DragMoveEvent, DragOverEvent, useDndMonitor, useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useIdeas, useTravelSegments, useUpdateTripDayInfo } from '@hooks'
import { formatCalendarDate } from '@lib/datetime'
import { EventCategory, GetIdeaResponse } from '@orbit/api-types'
import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import AddEventModal from '../AddEventModal'
import Card from '../Card'
import DropZone from '../DropZone'
import EditDayInfoModal from '../EditDayInfoModal'
import ImportIdeaModal from '../ImportIdeaModal'
import { RailConnector } from '../timelineRail.styles'
import TravelSegment from '../TravelSegment'
import { pickSegmentRoute } from '../TravelSegment/segmentDisplay'
import { TRAVEL_CHIP_ANCHOR } from '../TravelSegment/TravelSegment.styles'
import {
    AddEventButton,
    AddNoteRow,
    ButtonsContainer,
    ColumnContainer,
    ColumnContent,
    ColumnHeader,
    ColumnTitle,
    CustomDayName,
    DateDisplay,
    DayNoteIcon,
    DayNoteRow,
    DayNoteText,
    DraggedEventPlaceholder,
    EditDayInfoButton,
    EmptyState,
    EmptyStateText,
    EVENT_DOT_ANCHOR,
    EventContent,
    EventDot,
    EventDotColumn,
    TimelineEventRow,
} from './Column.styles'
import { ColumnProps } from './types'

const Column: React.FC<ColumnProps> = ({ day, tripId, onAddEvent, onUpdateEvent, onDeleteEvent }) => {
	const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false)
	const [isImportIdeaModalOpen, setIsImportIdeaModalOpen] = useState(false)
	const [isEditDayInfoModalOpen, setIsEditDayInfoModalOpen] = useState(false)
	const updateDayInfo = useUpdateTripDayInfo()
	const [prefilledEventData, setPrefilledEventData] = useState<{
		title?: string
		description?: string
		category?: EventCategory
		locationPrimaryText?: string
		links?: string[]
		startTime?: string
		createdFromTripIdeaId?: number
	} | undefined>(undefined)
	const columnRef = useRef<HTMLDivElement>(null)

	const { data: ideasData } = useIdeas(tripId)
	const { data: travelSegmentsData } = useTravelSegments(tripId)
	const [activeZone, setActiveZone] = useState<number | null>(null)
	const [isDroppingOnColumn, setIsDroppingOnColumn] = useState<boolean>(false)
	const [initialMousePos, setInitialMousePos] = useState<{ x: number; y: number } | null>(null)
	const [isDragSessionActive, setIsDragSessionActive] = useState<boolean>(false)
	const [activeDragEventId, setActiveDragEventId] = useState<string | null>(null)

	const { setNodeRef } = useDroppable({
		id: `day-${day.id}`,
	})

	useDndMonitor({
		onDragStart: event => {
			if (event?.activatorEvent) {
				const mouseEvent = event.activatorEvent as MouseEvent
				setInitialMousePos({ x: mouseEvent.clientX, y: mouseEvent.clientY })
			}
			const activeId = event.active?.id as string
			if (activeId?.startsWith('event-')) {
				const eventId = activeId.replace('event-', '').trim()
				const isInThisColumn = day.events.some(e => e.id.toString() === eventId)
				setActiveDragEventId(isInThisColumn ? eventId : null)
			}
			setIsDragSessionActive(true)
		},
		onDragOver: (event: DragOverEvent) => {
			const overId = event.over?.id as string

			if (!overId) {
				setActiveZone(null)
				setIsDroppingOnColumn(false)
				return
			}

			if (overId.startsWith('event')) {
				const eventId = overId.replace('event-', '').trim()
				setIsDroppingOnColumn(false)

				const eventIsInThisColumn = day.events.some(event => event.id.toString() === eventId)

				if (!eventIsInThisColumn) {
					setActiveZone(null)
				}
			}

			if (overId.startsWith('day')) {
				setActiveZone(null)

				const dayIndex = overId.replace('day-', '').trim()
				setIsDroppingOnColumn(dayIndex === day.id)
			}
		},
		onDragMove: (event: DragMoveEvent) => {
			const overId = event.over?.id as string

			if (!overId) {
				setActiveZone(null)
				return
			}

			if (!overId.startsWith('event')) {
				return
			}

			const eventId = overId.replace('event-', '').trim()
			const eventIsInThisColumn = day.events.some(event => event.id.toString() === eventId)

			if (!eventIsInThisColumn) {
				return
			}

			setIsDroppingOnColumn(false)

			const cardElement = columnRef.current?.querySelector(
				`[data-event-id="${eventId}"]`,
			) as HTMLElement

			if (!cardElement || !initialMousePos?.y) {
				return
			}

			const cardEventIndex = parseInt(cardElement.getAttribute('data-index') || '0')
			const cardRect = cardElement.getBoundingClientRect()

			const currentMouseY = initialMousePos.y + event.delta.y

			const relativeY = currentMouseY - cardRect.top
			const normalizedPosition = (relativeY / cardRect.height) * 100
			const clampedPosition = Math.max(0, Math.min(100, normalizedPosition))

			setActiveZone(cardEventIndex + (clampedPosition < 50 ? 0 : 1))
		},
		onDragEnd: (dragEvent: DragEndEvent) => {
			if (!dragEvent.active?.id || !dragEvent.over?.id) {
				resetDragStateVars()
				return
			}

			const activeId = dragEvent.active.id as string
			const overId = dragEvent.over.id as string

			if (!activeId.startsWith('event')) {
				resetDragStateVars()
				return
			}

			const activeEventId = activeId.replace('event-', '').trim()

			if (overId.startsWith('event')) {
				const overEventId = overId.replace('event-', '').trim()

				const overEventIndexWithinThisDay = day.events.findIndex(
					event => event.id.toString() === overEventId,
				)

				if (overEventIndexWithinThisDay !== -1 && activeZone != null) {
					const activeEventIndexWithinThisDay = day.events.findIndex(
						event => event.id.toString() === activeEventId,
					)

					const targetEventIndex =
						activeEventIndexWithinThisDay !== -1 &&
						activeEventIndexWithinThisDay < overEventIndexWithinThisDay
							? activeZone - 1
							: activeZone

					onUpdateEvent(activeEventId, {
						tripDayIndex: Number(day.id),
						eventIndex: targetEventIndex,
					})
				}
			} else if (overId.startsWith('day')) {
				const overDayId = overId.replace('day-', '').trim()
				if (day.id === overDayId) {
					onUpdateEvent(activeEventId, {
						tripDayIndex: Number(day.id),
						eventIndex: 0,
					})
				}
			}

			setActiveZone(null)
			setIsDroppingOnColumn(false)
			setIsDragSessionActive(false)
			setActiveDragEventId(null)
		},
	})

	const resetDragStateVars = () => {
		setActiveZone(null)
		setIsDroppingOnColumn(false)
		setIsDragSessionActive(false)
		setActiveDragEventId(null)
	}

	const handleAddEvent = (
		title: string,
		description: string,
		category?: EventCategory,
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		},
		links?: string[],
		startTime?: string | null,
		createdFromTripIdeaId?: number,
		pendingFiles?: File[],
	) => {
		onAddEvent(day.id, title, description, category, location, links, startTime, createdFromTripIdeaId, pendingFiles)
		setPrefilledEventData(undefined)
	}

	const handleCloseModal = () => {
		setIsAddEventModalOpen(false)
		setPrefilledEventData(undefined)
	}

	const handleImportIdea = (idea: GetIdeaResponse) => {
		setIsImportIdeaModalOpen(false)

		setPrefilledEventData({
			title: idea.name,
			description: idea.description || '',
			category: idea.category,
			locationPrimaryText: idea.location?.primaryText || '',
			links: idea.links?.map(link => link.url) || [],
			startTime: '',
			createdFromTripIdeaId: idea.id,
		})

		setIsAddEventModalOpen(true)
	}

	const handleCloseImportModal = () => {
		setIsImportIdeaModalOpen(false)
	}

	const handleSaveDayInfo = async (name: string | null, note: string | null) => {
		try {
			await updateDayInfo.mutateAsync({
				tripId,
				dayIndex: Number(day.id),
				data: { name, note },
			})
			setIsEditDayInfoModalOpen(false)
			toast.success('Day info updated')
		} catch {
			toast.error('Failed to save day info. Please try again.')
		}
	}

	const getTravelSegmentBetweenEvents = (currentEventId: number, nextEventId: number) => {
		if (!travelSegmentsData?.segments) return null

		return travelSegmentsData.segments.find(
			segment =>
				segment.fromEventId === currentEventId &&
				segment.toEventId === nextEventId &&
				segment.tripDayIndex === Number(day.id),
		)
	}

	const sortedEvents = day.events

	return (
		<ColumnContainer $isDraggingOver={isDroppingOnColumn}>
			<ColumnHeader>
				<ColumnTitle>
					{day.displayName}
					<DateDisplay>{formatCalendarDate(day.date, 'weekday')}</DateDisplay>
					{day.name && <CustomDayName>{day.name}</CustomDayName>}
				</ColumnTitle>
				<EditDayInfoButton
					type="button"
					onClick={() => setIsEditDayInfoModalOpen(true)}
					title="Edit day info"
				>
					✎ Edit
				</EditDayInfoButton>
			</ColumnHeader>

			{day.note ? (
				<DayNoteRow type="button" onClick={() => setIsEditDayInfoModalOpen(true)}>
					<DayNoteIcon>📝</DayNoteIcon>
					<DayNoteText>{day.note}</DayNoteText>
				</DayNoteRow>
			) : (
				<AddNoteRow type="button" onClick={() => setIsEditDayInfoModalOpen(true)}>
					<DayNoteIcon>📝</DayNoteIcon>
					Add a note…
				</AddNoteRow>
			)}

			<ColumnContent ref={day.events.length > 0 ? null : setNodeRef}>
				<SortableContext
					items={sortedEvents.map(event => `event-${event.id}`)}
					strategy={verticalListSortingStrategy}
				>
					<div ref={columnRef}>
					{sortedEvents.map((event, index) => {
						const nextEvent = sortedEvents[index + 1]
						const travelSegment = nextEvent
							? getTravelSegmentBetweenEvents(event.id, nextEvent.id)
							: null
						// The chip row renders only when the segment has a displayable
						// route — the connector must aim at whichever row actually follows.
						const shownSegment =
							travelSegment && pickSegmentRoute(travelSegment) ? travelSegment : null
						const isLastEvent = index === sortedEvents.length - 1
						const isBeingDragged = activeDragEventId === event.id.toString()

						return (
							<React.Fragment key={event.id}>
								<DropZone isExpanded={activeZone === index} />
								{isBeingDragged ? (
									<DraggedEventPlaceholder>
										<EventContent data-event-id={event.id} data-index={index}>
											<Card event={event} onUpdate={onUpdateEvent} onDelete={onDeleteEvent} />
										</EventContent>
									</DraggedEventPlaceholder>
								) : (
									<TimelineEventRow>
										<EventDotColumn>
											{/* One dash run per gap, from this dot into the next
											    row's anchor (travel chip or next dot). */}
											{!isLastEvent && (
												<RailConnector
													$from={EVENT_DOT_ANCHOR}
													$into={shownSegment ? TRAVEL_CHIP_ANCHOR : EVENT_DOT_ANCHOR}
												/>
											)}
											<EventDot />
										</EventDotColumn>
										<EventContent data-event-id={event.id} data-index={index}>
											<Card event={event} onUpdate={onUpdateEvent} onDelete={onDeleteEvent} />
										</EventContent>
									</TimelineEventRow>
								)}
								{shownSegment && (
									<TravelSegment segment={shownSegment} isHidden={isDragSessionActive} />
								)}
								{isLastEvent && (
									<DropZone isExpanded={activeZone === sortedEvents.length} />
								)}
							</React.Fragment>
						)
					})}
					</div>
				</SortableContext>

				{day.events.length === 0 && !isAddEventModalOpen && (
					<EmptyState>
						<EmptyStateText>No Events Yet</EmptyStateText>
					</EmptyState>
				)}
			</ColumnContent>

			<ButtonsContainer>
				<AddEventButton onClick={() => setIsAddEventModalOpen(true)}>
					<CrossIcon sx={{ fontSize: 18 }} />
					Add
				</AddEventButton>
				{ideasData?.ideas && ideasData.ideas.length > 0 && (
					<AddEventButton onClick={() => setIsImportIdeaModalOpen(true)}>
						<IdeaIcon sx={{ fontSize: 18 }} />
						Import Idea
					</AddEventButton>
				)}
			</ButtonsContainer>

		<AddEventModal
			isOpen={isAddEventModalOpen}
			onClose={handleCloseModal}
			onSubmit={handleAddEvent}
			dayName={day.date}
			initialValues={prefilledEventData}
		/>

	<ImportIdeaModal
		isOpen={isImportIdeaModalOpen}
		onClose={handleCloseImportModal}
		onNext={handleImportIdea}
		ideas={ideasData?.ideas || []}
	/>

	<EditDayInfoModal
		isOpen={isEditDayInfoModalOpen}
		onClose={() => setIsEditDayInfoModalOpen(false)}
		onSave={handleSaveDayInfo}
		isSaving={updateDayInfo.isPending}
		initialName={day.name}
		initialNote={day.note}
		dayLabel={formatCalendarDate(day.date, 'weekday')}
	/>
	</ColumnContainer>
)
}

export default Column
